import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  InputNumber,
  Button,
  notification,
  Modal,
  Skeleton,
  Tag,
  Image,
  Space,
  Divider,
  Tooltip,
  Card,
  Badge,
  Breadcrumb,
  Grid
} from 'antd';
import './styles.scss';
import {
  ShoppingCartOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  ShopOutlined,
  HomeOutlined,
  TagOutlined,
  BarcodeOutlined,
  StockOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import LayoutPage from '../../layouts/Main';
import MarketPlaceService from '../../utils/api/marketplace';

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

export default function Products(props) {
  const { category, productId } = useParams();
  const user = JSON.parse(localStorage.getItem('user'));
  let role = localStorage.getItem('role');
  let history = useNavigate();
  const screens = useBreakpoint();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [pendingCartItem, setPendingCartItem] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await MarketPlaceService.getProductById(productId);
        if (response.data) {
          if (response.data.product) {
            setProduct(response.data.product);
          } else if (response.data.item) {
            setProduct({
              ...response.data.item[0]?.product,
              supplier: response.data.item[0]?.supplier
            } || response.data);
          } else {
            setProduct(response.data);
          }
        } else {
          setProduct(response);
        }
      } catch (error) {
        notification.error({
          message: 'Error',
          description: 'No se pudo cargar el producto'
        });
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  const formatText = (text) => {
    return text
      ? text.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      : '';
  };

  const formattedCategory = formatText(category);

  const handleQuantityChange = (value) => {
    setQuantity(value || 1);
  };

  const getDisplayPrice = () => {
    if (role === '6') {
      return product.price || '0';
    }
    return product.newPrice || product.price || '0';
  };

  const getCartPrice = () => {
    if (role === '6') {
      return Number(product.price) || 0;
    }
    return product.newPrice && product.newPrice !== product.price
      ? Number(product.newPrice)
      : Number(product.price) || 0;
  };

  const handleAddToCart = async () => {
    if (!product) return;
    if (!user) {
      const price = getCartPrice();
      setPendingCartItem({
        id: product.id,
        product_id: product.id,
        supplier_id: product.supplier_id || 'default-supplier',
        name: product.name,
        category: product.category,
        sub_category: product.sub_category || product.subcategory || 'general',
        quantity: quantity,
        price: price,
        imgs: product.images || [],
        description: product.description || ''
      });
      setIsLoginModalOpen(true);
      return;
    }

    try {
      setAddingToCart(true);
      const price = getCartPrice();
      const totalPrice = price * quantity;
      let cartResponse;
      try {
        cartResponse = await MarketPlaceService.getCartByClient(user.id);
      } catch (error) {
        cartResponse = { data: null };
      }

      let currentCart = cartResponse.data;
      let productsInCart = [];

      if (currentCart && currentCart.products && Array.isArray(currentCart.products)) {
        productsInCart = [...currentCart.products];

        const existingProductIndex = productsInCart.findIndex(item =>
          item.id === product.id || item.product_id === product.id
        );

        if (existingProductIndex > -1) {
          productsInCart[existingProductIndex].quantity += quantity;
          productsInCart[existingProductIndex].price = price;
        } else {
          const newProduct = {
            id: product.id,
            product_id: product.id,
            supplier_id: product.supplier_id || 'default-supplier',
            name: product.name,
            category: product.category,
            sub_category: product.sub_category || product.subcategory || 'general',
            quantity: quantity,
            price: price,
            imgs: product.images || [],
            description: product.description || '',
            user_role: user.role,
            user_id: user.id
          };
          productsInCart.push(newProduct);
        }
      } else {
        const newProduct = {
          id: product.id,
          product_id: product.id,
          supplier_id: product.supplier_id || 'default-supplier',
          name: product.name,
          category: product.category,
          sub_category: product.sub_category || product.subcategory || 'general',
          quantity: quantity,
          price: price,
          imgs: product.images || [],
          description: product.description || '',
          user_role: user.role,
          user_id: user.id
        };
        productsInCart = [newProduct];
      }

      const totalCartPrice = productsInCart.reduce((sum, item) =>
        sum + (Number(item.price) * Number(item.quantity)), 0
      );
      const cartData = {
        client_id: user.id,
        client_role: user.role,
        products: productsInCart,
        price: totalCartPrice,
        change_type: 'MX',
        status: 1
      };

      let result;
      if (currentCart && currentCart.id) {
        result = await MarketPlaceService.updateCart(cartData, currentCart.id);
      } else {
        result = await MarketPlaceService.addProductToCart(cartData);
      }
      const cartKey = `cart_${user.id}_${user.role}`;
      localStorage.setItem(cartKey, JSON.stringify(productsInCart));

      window.dispatchEvent(new Event('cartUpdated'));

      notification.success({
        message: 'Producto agregado al carrito',
        description: (
          <div>
            <p><strong>{product.name}</strong></p>
            <p>Cantidad: {quantity}</p>
            <p>Total: ${totalPrice.toFixed(2)} MXN</p>
          </div>
        ),
        icon: <ShoppingCartOutlined style={{ color: '#52c41a' }} />,
        placement: 'topRight',
        duration: 4,
      });

    } catch (error) {

      let errorMessage = 'No se pudo agregar el producto al carrito';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.request) {
        errorMessage = 'Error de conexión. Verifica tu internet.';
      }

      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
        duration: 4,
      });
    } finally {
      setAddingToCart(false);
    }
  };

  const renderSupplierTooltip = () => {
    if (!product.supplier) {
      return <Text>Información del proveedor no disponible</Text>;
    }

    const supplier = product.supplier;
    return (
      <div style={{ minWidth: '200px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <ShopOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          <Text strong>{supplier.bussiness || 'Sin nombre de negocio'}</Text>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <UserOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
          <Text>{supplier.name || 'No especificado'}</Text>
        </div>

        {supplier.email && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <MailOutlined style={{ marginRight: '8px', color: '#fa8c16' }} />
            <Text>{supplier.email}</Text>
          </div>
        )}

        {supplier.phone && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <PhoneOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
            <Text>{supplier.phone}</Text>
          </div>
        )}

        {supplier.city && supplier.state && (
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {supplier.city}, {supplier.state}, {supplier.country}
            </Text>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <LayoutPage>
        <div style={{
          padding: screens.xs ? '16px' : '24px',
          backgroundColor: 'white',
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Row gutter={[24, 24]} style={{ width: '100%' }}>
            <Col xs={24} md={12}>
              <Skeleton.Image
                active
                style={{
                  width: '100%',
                  height: screens.xs ? 250 : screens.md ? 350 : 400,
                  borderRadius: '12px'
                }}
              />
            </Col>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 1 }} title={{ width: '70%' }} />
              <Skeleton active paragraph={{ rows: 3 }} />
              <Skeleton active paragraph={{ rows: 2 }} />
              <div style={{ marginTop: '24px' }}>
                <Skeleton.Button active style={{ width: screens.xs ? 180 : 200, height: 45 }} />
              </div>
            </Col>
          </Row>
        </div>
      </LayoutPage>
    );
  }

  if (!product) {
    return (
      <LayoutPage>
        <div style={{
          textAlign: 'center',
          padding: screens.xs ? '40px 16px' : '80px',
          backgroundColor: 'white',
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Title level={2} style={{
            fontSize: screens.xs ? '24px' : '32px',
            marginBottom: '16px'
          }}>
            Producto no encontrado
          </Title>
          <Paragraph style={{
            marginBottom: '32px',
            fontSize: screens.xs ? '14px' : '16px'
          }}>
            El producto que buscas no está disponible.
          </Paragraph>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => history('/marketplace')}
            size={screens.xs ? 'middle' : 'large'}
          >
            Volver al Marketplace
          </Button>
        </div>
      </LayoutPage>
    );
  }

  const displayPrice = getDisplayPrice();
  const isOutOfStock = product.stock <= 0;
  const isArchived = product.archive;
  const isAdmin = role === '1';
  const isSupplier = role === '6';

  const breadcrumbItems = [
    {
      title: <Button
        type="text"
        icon={<HomeOutlined />}
        onClick={() => history('/marketplace')}
        size="small"
      >
        Marketplace
      </Button>
    },
    {
      title: <span style={{ color: '#666' }}>{product.category}</span>
    },
    {
      title: <span style={{ fontWeight: 500 }}>{product.name}</span>
    }
  ];

  const savePendingCartItem = () => {
    if (!pendingCartItem) return;
    const pendingKey = 'pending_cart_items';
    let pendingItems = [];

    try {
      const existing = localStorage.getItem(pendingKey);
      if (existing) {
        pendingItems = JSON.parse(existing);
      }
    } catch (error) {
      pendingItems = [];
    }

    const existingIndex = pendingItems.findIndex(item =>
      item.id === pendingCartItem.id || item.product_id === pendingCartItem.product_id
    );

    if (existingIndex > -1) {
      pendingItems[existingIndex].quantity += Number(pendingCartItem.quantity || 1);
      pendingItems[existingIndex].price = Number(pendingCartItem.price) || pendingItems[existingIndex].price || 0;
    } else {
      pendingItems.push(pendingCartItem);
    }

    localStorage.setItem(pendingKey, JSON.stringify(pendingItems));
    localStorage.setItem('post_login_redirect', '/marketplace/shopping-cart');
  };

  const handleLoginRedirect = () => {
    savePendingCartItem();
    history('/');
  };

  const handleRegisterRedirect = () => {
    savePendingCartItem();
    history('/register');
  };

  const renderLoginModal = () => (
    <Modal
      open={isLoginModalOpen}
      onCancel={() => setIsLoginModalOpen(false)}
      footer={null}
      centered
      closable
      width={420}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ background: '#0F5197', color: '#fff', padding: '28px 24px', textAlign: 'center' }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.15)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12
        }}>
          <ShoppingCartOutlined style={{ fontSize: 28, color: '#fff' }} />
        </div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          !Inicia sesion para continuar!
        </h2>
        <p style={{ margin: '8px 0 0', fontSize: 14, opacity: 0.95 }}>
          Crea una cuenta o inicia sesion para agregar productos a tu carrito
        </p>
      </div>
      <div style={{ padding: '18px 24px 24px' }}>
        <Button
          type="primary"
          block
          size="large"
          onClick={handleRegisterRedirect}
          style={{ background: '#0F5197', borderColor: '#0F5197', fontWeight: 600 }}
        >
          Crear cuenta
        </Button>
        <Button
          block
          size="large"
          onClick={handleLoginRedirect}
          style={{ marginTop: 12, fontWeight: 600 }}
        >
          Ya tengo cuenta
        </Button>
        <div style={{ marginTop: 12, fontSize: 12, textAlign: 'center', color: '#8a8a8a' }}>
          Al continuar, aceptas nuestros terminos de servicio y politica de privacidad
        </div>
      </div>
    </Modal>
  );

  return (
    <LayoutPage>
      {renderLoginModal()}
      <div style={{
        backgroundColor: 'white',
        padding: screens.xs ? '16px' : screens.md ? '20px' : '24px',
        minHeight: '100vh'
      }}>
        <Breadcrumb
          items={breadcrumbItems}
          style={{
            marginBottom: screens.xs ? '20px' : '32px',
            padding: screens.xs ? '0' : '0 16px',
            fontSize: screens.xs ? '12px' : '14px'
          }}
        />

        <Row gutter={screens.xs ? [16, 16] : screens.md ? [24, 24] : [32, 32]}>
          <Col xs={24} md={12} lg={10}>
            <div style={{ position: 'relative' }}>
              <Badge.Ribbon
                text={
                  isSupplier ? "PROVEEDOR" :
                    isAdmin ? "ADMIN" :
                      isArchived ? "ARCHIVADO" :
                        isOutOfStock ? "AGOTADO" : "DISPONIBLE"
                }
                color={
                  isSupplier ? "#fa8c16" :
                    isAdmin ? "#722ed1" :
                      isArchived ? "#faad14" :
                        isOutOfStock ? "#ff4d4f" : "#52c41a"
                }
                style={{
                  top: screens.xs ? '10px' : '16px',
                  fontSize: screens.xs ? '10px' : '12px',
                  fontWeight: '600'
                }}
              >
                <Card
                  hoverable
                  style={{
                    borderRadius: screens.xs ? '8px' : '12px',
                    border: '1px solid #f0f0f0',
                    overflow: 'hidden',
                    marginBottom: screens.xs ? '16px' : '24px'
                  }}
                  bodyStyle={{ padding: 0 }}
                >
                  <div style={{
                    padding: screens.xs ? '20px' : '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                    minHeight: screens.xs ? 250 : screens.md ? 300 : 400
                  }}>
                    {product.images && product.images.length > 0 ? (
                      <Image
                        src={product.images[selectedImageIndex]}
                        alt={product.name}
                        style={{
                          width: '100%',
                          maxWidth: '500px',
                          height: 'auto',
                          maxHeight: screens.xs ? 250 : screens.md ? 300 : 400,
                          borderRadius: screens.xs ? '6px' : '8px',
                          objectFit: 'contain'
                        }}
                        preview={{
                          mask: <div style={{
                            padding: '6px 12px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: screens.xs ? '11px' : '13px'
                          }}>Ver ampliado</div>
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: screens.xs ? 250 : screens.md ? 300 : 400,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#bfbfbf'
                      }}>
                        <ShopOutlined style={{
                          fontSize: screens.xs ? '48px' : '64px',
                          marginBottom: '12px'
                        }} />
                        <Text type="secondary" style={{ fontSize: screens.xs ? '13px' : '14px' }}>
                          Sin imagen disponible
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>
              </Badge.Ribbon>

              {product.images && product.images.length > 1 && (
                <Row gutter={[6, 6]} style={{ marginTop: '12px' }}>
                  {product.images.map((img, index) => (
                    <Col xs={6} sm={4} md={6} lg={6} key={index}>
                      <Card
                        hoverable
                        onClick={() => setSelectedImageIndex(index)}
                        style={{
                          borderRadius: '6px',
                          border: selectedImageIndex === index ? '2px solid #1890ff' : '1px solid #f0f0f0',
                          padding: '2px',
                          cursor: 'pointer'
                        }}
                        bodyStyle={{ padding: 0 }}
                      >
                        <div style={{
                          height: screens.xs ? 60 : screens.sm ? 70 : 80,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fafafa',
                          borderRadius: '4px'
                        }}>
                          <img
                            src={img}
                            alt={`Vista ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          </Col>

          <Col xs={24} md={12} lg={14}>
            <Space direction="vertical" size={screens.xs ? 'middle' : 'large'} style={{ width: '100%' }}>
              <div>
                <Title
                  level={1}
                  style={{
                    fontSize: screens.xs ? '22px' : screens.md ? '28px' : '36px',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    marginBottom: screens.xs ? '12px' : '16px',
                    color: '#333'
                  }}
                >
                  {product.name}
                </Title>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: screens.xs ? '8px' : '12px',
                  marginBottom: screens.xs ? '16px' : '24px',
                  flexWrap: 'wrap'
                }}>
                  <Tag
                    icon={screens.xs ? null : <TagOutlined />}
                    color="blue"
                    style={{
                      fontSize: screens.xs ? '11px' : '12px',
                      padding: screens.xs ? '4px 8px' : '6px 12px',
                      margin: 0
                    }}
                  >
                    {product.category}
                  </Tag>
                  {product.sub_category && (
                    <Tag
                      color="geekblue"
                      style={{
                        fontSize: screens.xs ? '11px' : '12px',
                        padding: screens.xs ? '4px 8px' : '6px 12px',
                        margin: 0
                      }}
                    >
                      {product.sub_category}
                    </Tag>
                  )}
                </div>
              </div>

              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: screens.xs ? '10px' : '12px',
                padding: screens.xs ? '16px' : '24px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ marginBottom: screens.xs ? '12px' : '16px' }}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: '#1890ff',
                      fontSize: screens.xs ? '28px' : screens.md ? '36px' : '42px',
                      fontWeight: 700,
                      lineHeight: 1
                    }}
                  >
                    ${typeof displayPrice === 'number' ? displayPrice.toFixed(2) : parseFloat(displayPrice || 0).toFixed(2)} MXN
                  </Title>

                  <Paragraph type="secondary" style={{
                    marginTop: '8px',
                    fontSize: screens.xs ? '13px' : '16px',
                    color: '#666'
                  }}>
                    IVA incluido • Envío gratis solo en Ciudad Juárez
                  </Paragraph>
                </div>

                {(role === '1' || role === '4') && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: screens.xs ? '12px' : '16px',
                    flexWrap: screens.xs ? 'wrap' : 'nowrap'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: screens.xs ? '1 0 100%' : '0 1 auto'
                    }}>
                      <Text strong style={{
                        fontSize: screens.xs ? '13px' : '14px',
                        whiteSpace: 'nowrap'
                      }}>
                        Cantidad:
                      </Text>
                      <InputNumber
                        min={1}
                        max={product.stock}
                        value={quantity}
                        onChange={handleQuantityChange}
                        disabled={isArchived || isOutOfStock}
                        style={{
                          width: screens.xs ? 70 : 80
                        }}
                        size={screens.xs ? 'small' : 'middle'}
                        controls={true}
                      />
                    </div>

                    <Button
                      type="primary"
                      icon={<ShoppingCartOutlined />}
                      loading={addingToCart}
                      onClick={handleAddToCart}
                      disabled={isArchived || isOutOfStock}
                      size={screens.xs ? 'middle' : 'large'}
                      style={{
                        flex: screens.xs ? '1 0 100%' : '0 1 auto',
                        height: screens.xs ? '40px' : '48px',
                        backgroundColor: '#00c46a',
                        borderColor: '#00c46a',
                        fontSize: screens.xs ? '14px' : '16px',
                        fontWeight: 600
                      }}
                    >
                      {addingToCart ? 'Agregando...' : 'Agregar al carrito'}
                    </Button>
                  </div>
                )}

                {isOutOfStock && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: '#fff2e8',
                    border: '1px solid #ffbb96',
                    borderRadius: '6px'
                  }}>
                    <Text style={{
                      fontSize: screens.xs ? '12px' : '13px',
                      color: '#d46b08'
                    }}>
                      Este producto está actualmente agotado.
                    </Text>
                  </div>
                )}

                {isArchived && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    backgroundColor: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: '6px'
                  }}>
                    <Text style={{
                      fontSize: screens.xs ? '12px' : '13px',
                      color: '#d48806'
                    }}>
                      Producto archivado - No disponible para compra.
                    </Text>
                  </div>
                )}
              </div>

              <Card
                title={
                  <span style={{ fontSize: screens.xs ? '16px' : '18px' }}>
                    Descripción
                  </span>
                }
                style={{
                  borderRadius: screens.xs ? '10px' : '12px',
                  marginTop: screens.xs ? '8px' : '0'
                }}
                bodyStyle={{
                  padding: screens.xs ? '20px' : '32px'
                }}
              >
                <Paragraph style={{
                  fontSize: screens.xs ? '16px' : '18px',
                  lineHeight: 1.6,
                  color: '#555',
                  margin: '5px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {product.description}
                </Paragraph>
              </Card>

              <Card
                title={
                  <span style={{ fontSize: screens.xs ? '16px' : '18px' }}>
                    Información del producto
                  </span>
                }
                style={{
                  borderRadius: screens.xs ? '10px' : '12px',
                  marginTop: screens.xs ? '8px' : '5px'
                }}
                bodyStyle={{
                  padding: screens.xs ? '20px' : '32px'
                }}
              >
                <div className="product-info-grid-container">
                  <div className="product-info-grid">
                    <div className="product-info-card">
                      <BarcodeOutlined className="product-info-icon" style={{ color: '#1890ff' }} />
                      <Text strong className="product-info-label">
                        SKU
                      </Text>
                      <Text className="product-info-value">
                        {product.sku || 'N/A'}
                      </Text>
                    </div>

                    <div className="product-info-card">
                      <StockOutlined className="product-info-icon" style={{
                        color: isOutOfStock ? '#ff4d4f' : '#52c41a'
                      }} />
                      <Text strong className="product-info-label">
                        STOCK
                      </Text>
                      <Text className="product-info-value" style={{
                        color: isOutOfStock ? '#ff4d4f' : '#52c41a'
                      }}>
                        {product.stock} unidades
                      </Text>
                    </div>

                    {(isAdmin || isSupplier) && (
                      <>
                        <div className="product-info-card">
                          <TagOutlined className="product-info-icon" style={{ color: '#722ed1' }} />
                          <Text strong className="product-info-label">
                            ID
                          </Text>
                          <Text className="product-info-value">
                            {product.id}
                          </Text>
                        </div>

                        <div className="product-info-card">
                          <ShopOutlined className="product-info-icon" style={{ color: '#fa8c16' }} />
                          <Text strong className="product-info-label">
                            PROVEEDOR
                          </Text>
                          <Tooltip
                            title={renderSupplierTooltip}
                            placement="top"
                            style={{
                              maxWidth: screens.xs ? 280 : 400,
                              backgroundColor: '#fff',
                              color: '#333',
                              boxShadow: '0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08), 0 9px 28px 8px rgba(0,0,0,0.05)'
                            }}
                            color="#fff"
                          >
                            <Button
                              type="link"
                              className="product-info-supplier-button"
                            >
                              {product.supplier?.bussiness || 'N/A'}
                            </Button>
                          </Tooltip>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </LayoutPage>
  );
}